import { TableFilter, TFilterValues, TTableFilter } from '@components/table/types';
import { ValueLabelPair } from '@components/table/types/DateMinMax.type';
import { formatCurrency } from '@utils/number';

/**
 * Generates custom filters based on applied filters and metadata.
 * @param filtersForChips - The list of filters to turn into chips
 * @param filters - The list of available filters.
 * @param entityMetadataArray - Metadata array for resolving labels.
 * @returns An array of custom filters with labels and values.
 */
export const generateChipLabelsFromFilters = ({
  filtersForChips,
  filters,
  entityMetadataArray,
}: {
  filtersForChips: TFilterValues | undefined;
  enabledFilters?: TFilterValues | undefined;
  filters: TTableFilter[] | undefined;
  entityMetadataArray: Record<string, { id: string; label: string; helperText?: string }>;
}): {
  label: string;
  value: any;
  filter: TTableFilter;
  idToFind?: string;
  index?: number;
}[] => {
  if (!filters || !filtersForChips) {
    return [];
  }

  return filters.reduce((acc: any, filter) => {
    const appliedValue = filtersForChips[filter.field];

    if (appliedValue) {
      switch (filter.type) {
        case TableFilter.CHECKBOX: {
          if (appliedValue.value == 'true') {
            //Only add if true
            acc.push({
              label: filter.label,
              value: 'Yes',
              filter,
            });
          }
          break;
        }
        case TableFilter.NUMBER_RANGE: {
          const numbers: { min?: number | string; max?: number | string } | undefined = appliedValue;
          if (!numbers) {
            break;
          }

          if (numbers.min && numbers.max) {
            acc.push({
              label: filter.label,
              value: `${formatCurrency(Number(numbers.min))} - ${formatCurrency(Number(numbers.max))}`,
              filter,
            });
          } else if (numbers.min) {
            acc.push({
              label: filter.label,
              value: `> ${formatCurrency(Number(numbers.min))}`,
              filter,
            });
          } else if (numbers.max) {
            acc.push({
              label: filter.label,
              value: `< ${formatCurrency(Number(numbers.max))}`,
              filter,
            });
          }
          break;
        }
        case TableFilter.DATE_RANGE: {
          const dates: { min?: Date | string; max?: Date | string } | undefined = appliedValue;
          if (!dates) {
            break;
          }

          if (dates.min && dates.max) {
            acc.push({
              label: filter.label,
              value: `${new Date(dates.min).toLocaleDateString()} - ${new Date(dates.max).toLocaleDateString()}`,
              filter,
            });
          } else if (dates.min) {
            acc.push({
              label: filter.label,
              value: `> ${new Date(dates.min).toLocaleDateString()}`,
              filter,
            });
          } else if (dates.max) {
            acc.push({
              label: filter.label,
              value: `< ${new Date(dates.max).toLocaleDateString()}`,
              filter,
            });
          }
          break;
        }
        default: {
          if (Array.isArray(appliedValue)) {
            appliedValue.forEach((value: string | ValueLabelPair, index: number) => {
              const valueId = typeof value === 'string' ? value : value?.value;
              const entityMetadataItem = entityMetadataArray[valueId];
              if (entityMetadataItem) {
                acc.push({
                  label: filter.label,
                  value: `${entityMetadataItem.label}${
                    entityMetadataItem.helperText ? ` - ${entityMetadataItem.helperText}` : ''
                  }`,
                  filter,
                  idToFind: valueId,
                  index,
                });
              } else {
                acc.push({
                  label: filter.label,
                  value: typeof value === 'string' ? value : value?.label,
                  filter,
                  idToFind: valueId,
                  index,
                });
              }
            });
          } else if (typeof appliedValue === 'string') {
            const entityMetadataItem = entityMetadataArray[appliedValue];
            if (entityMetadataItem) {
              acc.push({
                label: filter.label,
                value: `${entityMetadataItem.label}${
                  entityMetadataItem.helperText ? ` - ${entityMetadataItem.helperText}` : ''
                }`,
                filter,
              });
            } else {
              acc.push({
                label: filter.label,
                value: appliedValue,
                filter,
              });
            }
          } else if (appliedValue?.label) {
            acc.push({
              label: filter.label,
              value: appliedValue?.label,
              filter,
            });
          }
        }
      }
    }
    return acc;
  }, []);
};
